import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useState, useCallback, useEffect } from "react";

type PosCustomerInfoDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (
    customerName: string,
    customerPhone: string,
    customerAddress?: string
  ) => void;
  mode?: "both" | "phoneOnly";
  initialName?: string;
  initialPhone?: string;
  initialAddress?: string;
};

export function PosCustomerInfoDialog({
  open,
  onOpenChange,
  onSubmit,
  mode = "both",
  initialName = "",
  initialPhone = "",
  initialAddress = "",
}: PosCustomerInfoDialogProps) {
  const [customerName, setCustomerName] = useState("");
  const [customerPhone, setCustomerPhone] = useState("");
  const [customerAddress, setCustomerAddress] = useState("");

  useEffect(() => {
    if (open) {
      setCustomerName(initialName);
      setCustomerPhone(initialPhone);
      setCustomerAddress(initialAddress ?? "");
    }
  }, [open, initialName, initialPhone, initialAddress]);

  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      const trimmedName =
        mode === "phoneOnly" ? (initialName || customerName).trim() : customerName.trim();
      const trimmedPhone = customerPhone.trim();
      if (!trimmedName || !trimmedPhone) return;
      onSubmit(trimmedName, trimmedPhone, customerAddress.trim() || undefined);
    },
    [mode, initialName, customerName, customerPhone, customerAddress, onSubmit]
  );

  const isPhoneOnly = mode === "phoneOnly";
  const isValid =
    isPhoneOnly
      ? Boolean(customerPhone.trim())
      : Boolean(customerName.trim() && customerPhone.trim());

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="top-4 translate-y-0 w-[90%] max-w-none sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Customer Information</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          {!isPhoneOnly && (
            <div className="space-y-2">
              <Label htmlFor="customerName">Customer Name</Label>
              <Input
                id="customerName"
                placeholder="Enter customer name"
                value={customerName}
                onChange={(e) => setCustomerName(e.target.value)}
                autoComplete="name"
              />
            </div>
          )}
          {isPhoneOnly && initialName && (
            <p className="text-sm text-muted-foreground">
              Customer: {initialName}
            </p>
          )}
          <div className="space-y-2">
            <Label htmlFor="customerPhone">Customer Phone</Label>
            <Input
              id="customerPhone"
              type="tel"
              placeholder="Enter phone number"
              value={customerPhone}
              onChange={(e) => setCustomerPhone(e.target.value)}
              autoComplete="tel"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="customerAddress">Address</Label>
            <Input
              id="customerAddress"
              placeholder="Enter delivery address"
              value={customerAddress}
              onChange={(e) => setCustomerAddress(e.target.value)}
              autoComplete="street-address"
            />
          </div>
          <DialogFooter>
            <Button type="submit" disabled={!isValid}>
              Continue to Pay
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
